import { createFileRoute } from '@tanstack/react-router'
import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import { X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { z } from 'zod'

import type { StockMovementListItem, StockMovementTypeType } from '@/api/admin-inventory-types'
import { preloadListStockMovements, preloadListInventoryLocations, useListStockMovements } from '@/api/react-query'
import { Badge } from '@/components/base/badge'
import { Container, ContainerHeader } from '@/components/base/container'
import { SearchField } from '@/components/base/search-field'
import { StatusCell } from '@/components/base/status-cell'
import { SingleColumnPage } from '@/components/page-layout'
import { FilterButton } from '@/components/table/filter-button'
import { DEFAULT_PAGE_SIZE, Pagination } from '@/components/table/pagination'
import { Table, useServerSort } from '@/components/table/table'
import { useI18n } from '@/i18n/i18n-react'
import { paginatedSearchWithSort } from '@/utils/route-utilities'

import { MovementFilterDrawer } from './-filter-drawer'

const MOVEMENT_TYPES = [
	'ADJUSTMENT',
	'RECEIPT',
	'TRANSFER_IN',
	'TRANSFER_OUT',
	'ORDER_DEDUCTION',
	'ORDER_RETURN'
] as const

export const Route = createFileRoute('/_authenticated/_general/inventory/movements')({
	component: InventoryMovementsPage,
	validateSearch: paginatedSearchWithSort(['createdAt', 'type', 'quantityDelta', 'quantityAfter', 'reason']).extend({
		inventoryLocationUuid: z.string().optional(),
		variantUuid: z.string().optional(),
		type: z.enum(MOVEMENT_TYPES).optional(),
		fromDate: z.coerce.number().optional(),
		toDate: z.coerce.number().optional()
	}),
	loaderDeps: ({
		search: { limit, offset, q, inventoryLocationUuid, variantUuid, type, fromDate, toDate, sortBy, sortOrder }
	}) => ({
		limit,
		offset,
		q,
		inventoryLocationUuid,
		variantUuid,
		type,
		fromDate,
		toDate,
		sortBy,
		sortOrder
	}),
	loader: async ({ context, deps }) => {
		await Promise.all([
			preloadListStockMovements(context, { query: deps }),
			preloadListInventoryLocations(context, { query: { limit: 100, offset: 0 } })
		])
	},
	staticData: {
		crumb: ({ context: { L } }) => L.Inventory.Movements()
	}
})

function InventoryMovementsPage() {
	const { L } = useI18n()
	const {
		limit = DEFAULT_PAGE_SIZE,
		offset,
		q,
		inventoryLocationUuid,
		variantUuid,
		type,
		fromDate,
		toDate,
		sortBy,
		sortOrder
	} = Route.useSearch()
	const navigate = Route.useNavigate()
	const [filterOpen, setFilterOpen] = useState(false)
	const serverSort = useServerSort(['createdAt', 'type', 'quantityDelta', 'quantityAfter', 'reason'])

	const movements = useListStockMovements({
		limit,
		offset,
		q,
		inventoryLocationUuid,
		variantUuid,
		type,
		fromDate,
		toDate,
		sortBy,
		sortOrder
	})

	const columnDefs = useMemo<ColDef<StockMovementListItem>[]>(
		() => [
			{
				headerName: L.Inventory.Column.Date(),
				field: 'createdAt',
				minWidth: 160,
				valueFormatter: ({ value }) => (value ? L.Date.DateAndTime(value) : '')
			},
			{
				headerName: L.Inventory.Column.Type(),
				field: 'type',
				minWidth: 140,
				cellRenderer: ({ value }: { value: StockMovementTypeType }) => (
					<StatusCell color='grey'>{L.Inventory.MovementType[value]()}</StatusCell>
				)
			},
			{ headerName: L.Inventory.Column.Product(), field: 'variant.productTitle', minWidth: 180, flex: 2 },
			{ headerName: L.Inventory.Column.Sku(), field: 'variant.sku', minWidth: 120, flex: 1 },
			{
				headerName: L.Inventory.Column.InventoryLocation(),
				field: 'inventoryLocation.name',
				minWidth: 140,
				flex: 1
			},
			{
				headerName: L.Inventory.Column.Delta(),
				field: 'quantityDelta',
				minWidth: 90,
				valueFormatter: ({ value }) => (value != null && value > 0 ? `+${value}` : String(value ?? ''))
			},
			{ headerName: L.Inventory.Column.QuantityAfter(), field: 'quantityAfter', minWidth: 110 },
			{
				headerName: L.Inventory.Column.Reference(),
				colId: 'reference',
				minWidth: 140,
				flex: 1,
				cellRenderer: ({ data }: ICellRendererParams<StockMovementListItem>) => {
					if (!data) return null
					const ref = data.orderUuid ?? data.purchaseOrderUuid ?? data.inventoryTransferUuid
					if (!ref) return <span className='text-ui-fg-muted'>—</span>
					return <span className='truncate font-mono text-xs'>{ref}</span>
				}
			},
			{ headerName: L.Inventory.Column.CreatedBy(), field: 'createdByUserName', minWidth: 120, flex: 1 },
			{ headerName: L.Inventory.Column.Reason(), field: 'reason', minWidth: 160, flex: 2 }
		],
		[L]
	)

	const activeFilterCount = [inventoryLocationUuid, type, fromDate, toDate].filter(Boolean).length

	return (
		<SingleColumnPage>
			<Container className='flex flex-1 flex-col overflow-hidden'>
				<ContainerHeader
					heading={L.Inventory.Movements()}
					rightSide={
						<>
							<SearchField />
							{variantUuid && (
								<Badge className='gap-1'>
									{L.Inventory.Column.Product()}
									<button
										type='button'
										className='hover:text-ui-fg-base'
										onClick={() =>
											navigate({
												search: prev => ({ ...prev, variantUuid: undefined, offset: 0 })
											})
										}
									>
										<X className='size-3' />
									</button>
								</Badge>
							)}
							<FilterButton onClick={() => setFilterOpen(true)} activeCount={activeFilterCount} />
						</>
					}
				/>
				<div className='min-w-0 flex-1 overflow-x-auto'>
					<Table<StockMovementListItem>
						className='w-full min-w-[900px] flex-1'
						data={movements.data?.stockMovements}
						columnUtilities={{}}
						columnDefs={columnDefs}
						serverSort={serverSort}
						animateRows
						suppressCellFocus
					/>
				</div>
				<div className='px-3 py-2 sm:px-6 sm:py-4'>
					<Pagination totalCount={movements.data?.count} />
				</div>
			</Container>

			<MovementFilterDrawer
				open={filterOpen}
				onOpenChange={setFilterOpen}
				defaultValues={{ inventoryLocationUuid, type, fromDate, toDate }}
				onApply={filters =>
					navigate({
						search: prev => ({
							...prev,
							inventoryLocationUuid: filters.inventoryLocationUuid,
							type: filters.type,
							fromDate: filters.fromDate,
							toDate: filters.toDate,
							offset: 0
						})
					})
				}
			/>
		</SingleColumnPage>
	)
}
